import { useEffect, useState } from "react";
import { Boxes, Plus, Edit2, Trash2, X, Search } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINR, formatNumber } from "@/utils/formatINR";
import DecimalInput from "@/components/DecimalInput";

export default function Materials() {
  const { user, canEditAll, isAdmin } = useAuth();
  // Engineer and manager both get row-edit. Delete stays admin-only via isAdmin().
  const canEditMaterialRow = canEditAll() || user?.role === "npd_engineer";
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/materials", { params: q ? { search: q } : {} });
      setRows(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const openNew = () => { setEditing(null); setShowForm(true); };
  const openEdit = (row) => { setEditing(row); setShowForm(true); };

  const del = async (row) => {
    if (!window.confirm(`Delete material "${row.grade}"?`)) return;
    try {
      await api.delete(`/materials/${row.material_id}`);
      toast.success("Material deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const grouped = rows.reduce((acc, r) => { (acc[r.category] ||= []).push(r); return acc; }, {});

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="materials-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Boxes size={22} className="text-orange-600" /> Materials <span className="text-base font-medium text-slate-400">({rows.length})</span></h1>
          <p className="text-sm text-slate-500 mt-0.5">Material grades, densities, and default rates.</p>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold rounded-md px-4 py-2" data-testid="material-new">
          <Plus size={16} /> New Material
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg">
        <div className="p-4 border-b border-slate-200">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-2.5 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search grade or category…"
                   className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
          </div>
        </div>
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-400">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400">No materials found.</div>
        ) : (
          <div data-testid="materials-list">
            {Object.entries(grouped).map(([cat, list]) => (
              <div key={cat}>
                <div className="px-4 py-2 bg-slate-50 text-xs uppercase tracking-wider text-slate-500 font-semibold border-b border-slate-100 flex items-center gap-2">
                  <span>{cat || "Uncategorised"}</span>
                  <span className="normal-case text-[10px] font-medium text-slate-400">· {list.length} grade{list.length === 1 ? "" : "s"}</span>
                </div>
                <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] text-sm">
                  <thead className="text-xs text-slate-500">
                    <tr>
                      <th className="text-left p-2.5 pl-4 font-medium w-10">#</th>
                      <th className="text-left p-2.5 font-medium">Grade</th>
                      <th className="text-right p-2.5 font-medium">Density (g/cc)</th>
                      <th className="text-right p-2.5 font-medium">RM Rate</th>
                      <th className="text-right p-2.5 font-medium">Scrap Rate</th>
                      <th className="w-20"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {list.map((r, idx) => (
                      <tr key={r.material_id} className="hover:bg-slate-50">
                        <td className="p-2.5 pl-4 font-mono text-xs text-slate-400">{idx + 1}</td>
                        <td className="p-2.5 font-medium text-slate-900">{r.grade}</td>
                        <td className="p-2.5 text-right font-mono text-slate-700">{formatNumber(r.density_g_cc, 2)}</td>
                        <td className="p-2.5 text-right font-mono text-slate-700">{formatINR(r.default_rm_rate_per_kg)}/kg</td>
                        <td className="p-2.5 text-right font-mono text-slate-700">{formatINR(r.default_scrap_rate_per_kg)}/kg</td>
                        <td className="p-2.5 text-right">
                          {canEditMaterialRow && (
                            <div className="flex gap-1 justify-end">
                              <button onClick={() => openEdit(r)} className="p-1.5 hover:bg-slate-200 rounded text-slate-500 hover:text-slate-700"><Edit2 size={14} /></button>
                              {isAdmin() && <button onClick={() => del(r)} className="p-1.5 hover:bg-red-50 rounded text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <MaterialForm
          initial={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
}

function MaterialForm({ initial, onClose, onSaved }) {
  const [f, setF] = useState(initial || { category: "", grade: "", density_g_cc: 7.85, default_rm_rate_per_kg: 0, default_scrap_rate_per_kg: 0, min_monthly_kg_threshold: 2000, notes: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (initial?.material_id) await api.put(`/materials/${initial.material_id}`, f);
      else await api.post("/materials", f);
      toast.success(initial ? "Material updated" : "Material created");
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white rounded-lg w-full max-w-lg shadow-xl" data-testid="material-form">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="font-semibold text-slate-900">{initial ? "Edit Material" : "New Material"}</h2>
          <button type="button" onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category *"><input required className="input" value={f.category} onChange={(e) => set("category", e.target.value)} placeholder="e.g. CR Steel" data-testid="material-category" /></Field>
            <Field label="Grade *"><input required className="input" value={f.grade} onChange={(e) => set("grade", e.target.value)} placeholder="e.g. SPCC" data-testid="material-grade" /></Field>
          </div>
          <Field label="Density (g/cc)"><DecimalInput className="input" value={f.density_g_cc} onChange={(v) => set("density_g_cc", v)} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Default RM Rate (₹/kg)"><DecimalInput className="input" value={f.default_rm_rate_per_kg} onChange={(v) => set("default_rm_rate_per_kg", v)} /></Field>
            <Field label="Default Scrap Rate (₹/kg)"><DecimalInput className="input" value={f.default_scrap_rate_per_kg} onChange={(v) => set("default_scrap_rate_per_kg", v)} /></Field>
          </div>
          <Field
            label="Min Monthly RM Threshold (kg/month)"
            hint="Below this, parts using this material are flagged for sourcing risk and require an MOQ commitment before approval."
          >
            <input
              type="number"
              step="100"
              className="input"
              value={f.min_monthly_kg_threshold ?? 2000}
              onChange={(e) => set("min_monthly_kg_threshold", e.target.value === "" ? null : (parseFloat(e.target.value) || 0))}
              data-testid="material-min-monthly-kg"
              placeholder="2000"
            />
          </Field>
          <Field label="Notes"><textarea rows={2} className="input" value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} /></Field>
        </div>
        <div className="p-5 border-t flex justify-end gap-2 bg-slate-50 rounded-b-lg">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm">Cancel</button>
          <button type="submit" disabled={busy} className="px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white text-sm font-semibold rounded-md" data-testid="material-save">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}
